import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ReportTheme } from '../../types';
import { getChartColor } from './chartUtils';

interface AvgEngagementTimeLineChartWidgetProps {
  data: any;
  data_points?: string[];
  report_start_date?: string;
  report_end_date?: string;
  reportTheme: ReportTheme;
}

export const AvgEngagementTimeLineChartWidget: React.FC<AvgEngagementTimeLineChartWidgetProps> = ({ 
  data, 
  data_points,
  report_start_date,
  report_end_date,
  reportTheme 
}) => {
  console.log('AvgEngagementTimeLineChartWidget raw data:', data);

  // Process the raw API data internally for Average Engagement Time
  const processData = () => {
    if (!data || !data.data || typeof data.data !== 'object') {
      console.warn('AvgEngagementTimeLineChartWidget: Invalid data structure');
      return null;
    }

    const rawData = data.data;
    const entries = Object.entries(rawData);
    
    if (entries.length === 0) {
      console.warn('AvgEngagementTimeLineChartWidget: No data entries found');
      return null;
    }

    const firstEntry = entries[0];
    const [, firstDayData] = firstEntry;
    
    if (!firstDayData || typeof firstDayData !== 'object' || !(firstDayData as any).date) {
      console.warn('AvgEngagementTimeLineChartWidget: Invalid data format');
      return null;
    }

    if (typeof (firstDayData as any).avg_session_duration !== 'number') {
      console.warn('AvgEngagementTimeLineChartWidget: Missing required metric (avg_session_duration)');
      return null;
    }
    
    const chartData = entries.map(([dateKey, dayData]: [string, any]) => {
      if (!dayData || typeof dayData !== 'object' || !dayData.date) {
        return null;
      }

      // Convert seconds to minutes for better readability
      const durationInMinutes = (dayData.avg_session_duration || 0) / 60;

      return {
        name: new Date(dayData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        duration: parseFloat(durationInMinutes.toFixed(2))
      };
    }).filter((item): item is { name: string; duration: number } => item !== null)
      .sort((a, b) => new Date(a.name).getTime() - new Date(b.name).getTime());

    return chartData.length > 0 ? chartData : null;
  };

  const processedData = processData();

  if (!processedData || processedData.length === 0) {
    return null;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={processedData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip 
          formatter={(value) => [`${value} min`, 'Duration']}
        />
        <Line 
          type="monotone" 
          dataKey="duration" 
          stroke={getChartColor(0, reportTheme.theme)} 
          strokeWidth={2} 
          name="Avg Engagement Time"
        />
      </LineChart>
    </ResponsiveContainer>
  );
};
